import classNames from "components/ui/classNames";
import { TabsContent, TabsList, TabsTrigger } from "components/ui/Tabs";
import React from "react";

interface CompanyContentContainerProps {
  children: React.ReactNode;
  className?: string;
}

export function CompanyContentContainer({ children, className }: CompanyContentContainerProps) {
  return (
    <div
      className={classNames(
        "relative flex h-full w-full rounded-xl bg-white shadow",
        "border border-slate-200",
        "ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
        "dark:border-none dark:bg-slate-900",
        "dark:ring-1 dark:ring-slate-950 dark:ring-offset-2 dark:ring-offset-slate-900",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface CompanyContainerProps {
  children: React.ReactNode;
  className?: string;
}

export function CompanyContainer({ children, className }: CompanyContainerProps) {
  return (
    <div
      className={classNames(
        "relative flex h-full w-full max-w-7xl flex-col gap-6 px-4",
        "xl:mx-auto xl:h-company-box xl:px-0",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function FluidCompanyContainer({ children, className }: CompanyContainerProps) {
  return (
    <div
      className={classNames(
        "relative flex h-full w-full max-w-7xl flex-col gap-6 px-4",
        "xl:mx-auto xl:h-auto xl:px-0",
        className,
      )}
    >
      {children}
    </div>
  );
}

type CompanyTab = {
  value: string;
  label: string;
  icon: React.ReactElement;
  disabled?: boolean;
};

interface CompanyTabsListProps {
  tabs: CompanyTab[];
}

export function CompanyTabsList({ tabs }: CompanyTabsListProps) {
  return (
    <TabsList
      className={classNames(
        "flex w-full flex-1 justify-start divide-x overflow-x-auto rounded-b-none rounded-t-xl p-0",
        "bg-slate-50",
        "dark:divide-slate-950 dark:border-slate-900 dark:bg-slate-800",
        "scrollbar-none",
      )}
    >
      {tabs
        .filter((tab) => !tab.disabled)
        .map((tab) => {
          return (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className={classNames(
                "h-28 min-w-32 justify-center overflow-hidden rounded-none md:h-fit md:w-full",
                "border-b border-b-transparent px-2",
                "border-b-slate-200 dark:border-b-slate-950",
                "group",
                {
                  "w-full": tabs.filter((tab) => !tab.disabled).length <= 3,
                },
              )}
            >
              <CompanyTab>
                <div
                  className={classNames(
                    "flex items-center justify-center rounded-full p-2 squircle",
                    "bg-slate-50 dark:bg-indigo-300/10 dark:text-indigo-200",
                    "group-data-[state=active]:dark:bg-indigo-300/20 group-data-[state=active]:dark:text-indigo-100",
                    "group-data-[state=active]:bg-indigo-50/50 group-data-[state=active]:text-indigo-600",
                  )}
                >
                  {tab.icon}
                </div>
                <h2
                  className={classNames(
                    "line-clamp-2 max-w-28 whitespace-pre-wrap text-center leading-6 lg:max-w-none lg:whitespace-nowrap lg:text-left",
                    "text-slate-500 group-data-[state=active]:font-bold",
                    "group-data-[state=active]:text-slate-600 group-data-[state=active]:dark:text-slate-200",
                  )}
                >
                  {tab.label}
                </h2>
              </CompanyTab>
            </TabsTrigger>
          );
        })}
    </TabsList>
  );
}

interface CompanyTabProps {
  children: React.ReactNode;
}

function CompanyTab({ children }: CompanyTabProps) {
  return <div className="flex flex-col items-center gap-2 truncate px-1.5 py-1 text-left md:flex-row">{children}</div>;
}

interface CompanyTabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function CompanyTabsContent({ value, children, className }: CompanyTabsContentProps) {
  return (
    <TabsContent className="mt-0 h-[calc(100%-51px)] w-full border-none p-0" value={value}>
      <div className={classNames("flex h-full w-full p-6", className)}>{children}</div>
    </TabsContent>
  );
}
